import { GameTrackOptions } from '../../model/tracking';
import {
  CloseStrategyEnum,
  GameCloseStrategies,
  PortalVerticalGameTilePropTypes,
} from '../../types/gameTypes';
import { CategoryPageTypes } from '../../types/pageTypes';
import {
  CategoryGameGridWithContentType,
  OverlayProps,
} from './../../types/category/categoryTypes';
import {
  GameLaunchStrategies,
  LaunchStrategyEnum,
  Table,
} from './../../types/gameTypes';
import {
  JackpotsV2ResponseType,
  MultipleJackpotV2Interface,
} from './../../types/jackpotTypes';

export interface ControllersDataItem {
  components: [
    string,
    {
      type: string;
      properties: any;
    }
  ][];
}

type ActionType =
  | 'STATIC'
  | 'BASECAMP'
  | 'GAME_DETAILS'
  | 'GAME_LAUNCH_MODAL'
  | 'SEARCH';

export interface ControllersData {
  categories?: ControllersDataItem;
  recentlyPlayed?: ControllersDataItem;
  search?: ControllersDataItem;
  promotions?: ControllersDataItem;
  gameDetails?: ControllersDataItem;
  gameLauncher?: ControllersDataItem;
  searchBar?: ControllersDataItem;
  bingoGames?: ControllersDataItem;
}

interface TrackingData {
  page?: {
    actionType: ActionType;
    SiteLanguage: string | null;
    SiteSubSection: string | null;
    SiteSectionCategory: string | null;
    SiteSectionSubCategory: string | null;
    PageGameName: string | null;
  };
  sidebar?: {
    actionType: ActionType;
    SiteLanguage: string | null;
    SiteSubSection: string | null;
    SiteSectionCategory: string | null;
    SiteSectionSubCategory: string | null;
    PageGameName: string | null;
  };
  SiteSection: string;
  ProductContext: string | null;
}

export type LogicContext = {
  appInitialized: boolean;
  mainLayoutPath: string;
  controllersData: ControllersData;
  navigationLinks: string[];
  currentLayout: Layout;
  currentScreenSize: 'small' | 'medium' | 'wide';
  initialRequest: boolean;
  restoreScroll: boolean;
  currentPageParams: string[];
  currentPagePath: string;
  pendingAccountEvents: object[];
  isAccountInfoAvailable: boolean;
  headerInitialized: boolean;
  rpFirstLoad: boolean;
  rpVisible: boolean;
  callbackMode: string;
  sessionResolved: boolean;
  segments?: string[] | null;
  gameLaunchStrategies: GameLaunchStrategies;
  gameCloseStrategies: GameCloseStrategies;
  trackingData: TrackingData;
  isErrorPage?: boolean;
  gameToLaunch?: {
    triggeredByUser: boolean;
    path?: string;
  } & PortalVerticalGameTilePropTypes;
  gamePendingAdditionToRecentlyPlayed?: PortalVerticalGameTilePropTypes;
  updates: Array<{ type: string; data: any }>;
  deviceInfo: DeviceInfo;
  games: {
    [key: string]: PortalVerticalGameTilePropTypes;
  };
  multiJackpots: Record<string, Record<string, MultipleJackpotV2Interface>>;
  currentJackpotsV2: Record<string, JackpotsV2ResponseType>;
  currentTablesIdList: string[];
  currentTables?: { [key: string]: Table };
  user: UserSessionInfo;
  loginModalState: {
    isOpened: boolean;
    isAwaitingLogin: boolean;
  };
  currentPageId?: string;
  previousPageArray: {
    pagePath: string;
    isInSidebar: boolean;
    trackingData: TrackingData['page'];
  }[];
  pages: CategoryPageTypes[];
  seo: {
    page: PageSeo;
    sidebar?: PageSeo;
  };
  search: SearchInfo;
  phrasePendingAdditionToRecentPhrases?: string;
  recentPhrases: string[];
  features: {
    [featureName: string]: FeatureConfig;
  };
  featureFlags: string;
  searchVisible: boolean;
  delayedTrackingAvailable: boolean;
  currentPageGames?: string[];
  isSidebarInLayout: boolean;
  changePageAction: string;
  recentlyPlayedSettings?: {
    view_all_button?: {
      label: string;
      route_link: string;
      notIndexed?: boolean;
    };
    view_all_button_visible: boolean;
    title: string;
    description: string;
    type: 'Single Block' | 'Grid';
  };
  overlay?: OverlayProps;
  whhAction: string;
  cookieBannerVisible: boolean;
  previousUserAccountNumber: string;
  inputConfig: {
    isIOS: boolean;
    isRenderedInIFrame: boolean;
  };
  personalizedBanners?: { selector: string; slug?: string }[];
  currentPersonalizedBanners?: { [key: string]: boolean };
  clickedOnSuggestedGame?: boolean;
  isFullScreen?: boolean;
  bingoGames?: PortalVerticalGameTilePropTypes[];
  isSwitchingGame?: boolean;
};

interface PageSeo {
  title: string;
  description: string;
  image: string;
  canonicalLinks: {
    origin: 'same' | 'cross';
    url: string;
  }[];
  noIndex: boolean;
  noTranslate: boolean;
  structuredData: string;
}

enum SourceTypeEnum {
  SEARCH_INPUT = 'Search Input',
  RECENT_SEARCHES = 'Recent Searches',
}

export type AuthTokenEvent = {
  type: string;
  data: {
    access_token?: string;
    expires_in?: number;
    refresh_token?: string;
    error?: string;
  };
};

export type SegmentsEvent = {
  type: string;
  data: {
    segments: string[] | null;
  };
};

export type GameLaunchEvent = {
  type: 'VIEW_CATEGORY_GAME_TILE_CLICK';
  payload: GameTrackOptions;
};

export type PromoClickEvent = {
  type: 'VIEW_CATEGORY_LINK_ITEM_CLICK';
  payload: {
    url?: string;
    path?: string;
    isBanner?: boolean;
    isPersonalizedBanner?: boolean;
  };
};

export type NavigationClickEvent = {
  type: 'VIEW_HEADER_MENU_ITEM_CLICK';
  payload: {
    path: string;
  };
};

export type SearchClickEvent = {
  type: 'VIEW_SEARCH_CLICK';
  payload: {
    value: string;
  };
};

export type SearchSubheaderClickEvent = {
  type: 'VIEW_SEARCH_SUBHEADER_ITEM_CLICK';
  payload: {
    index: number;
    type: string;
  };
};

export type FeatureModeType = 'enabled' | 'disabled' | 'hidden';
export type FeatureStateType = 'active' | 'inactive';

export type FeatureConfig = {
  mode: FeatureModeType;
  state: FeatureStateType;
};

export type UpdateGamesEvent = {
  type: '';
  data: { games: { [key: string]: PortalVerticalGameTilePropTypes } };
};

export type UpdateRecentlyPlayedEvent = {
  type: '';
  data: {
    recentlyPlayedGamesList: PortalVerticalGameTilePropTypes[];
  };
};

export type UpdateBingoGamesEvent = {
  type: '';
  data: {
    bingoGames: PortalVerticalGameTilePropTypes[];
  };
};

export type UpdateJackpotsEvent = {
  type: '';
  data: {
    jackpots: {
      [key: string]: {
        state: 'active' | 'normal' | 'won';
      };
    };
    multiJackpots: Record<string, any>;
  };
};

export type UpdateTablesEvent = {
  type: '';
  data: {
    liveDealerTables: {
      [key: string]: Table;
    };
  };
};

export type UpdateJackpotsV2Event = {
  type: '';
  data: {
    jackpots: Record<string, JackpotsV2ResponseType>;
  };
};

export type UpdateLaunchStrategiesEvent = {
  type: '';
  data: {
    gameLaunchStrategies: LaunchStrategyEnum[];
  };
};

export type UpdateCloseStrategiesEvent = {
  type: '';
  data: {
    gameCloseStrategies: CloseStrategyEnum[];
  };
};

export type UpdatePersonalizedBannersEvent = {
  type: '';
  payload: {
    banners: { container: string; rendered: boolean }[];
  };
};

export type DeviceInfo = {
  appVersion?: string;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  isNative: boolean;
  nativeDeviceOs: 'ios' | 'android' | null;
  deviceOs: DeviceOs;
};

export type DeviceOs = DeviceOsValues | null;

export enum DeviceOsValues {
  Windows = 'windows',
  IOS = 'ios',
  OSX = 'os x',
  Android = 'android',
  Linux = 'linux',
}

export type UserSessionInfo = {
  loggedIn: boolean;
  userCurrency: string;
  userAccountNumber: string;
  userStatus: 'customer' | 'non-customer';
  userToken?: string;
  authToken?: {
    access_token?: string; // auth token internal/external flow
    refresh_token?: string; // auth token internal flow
    expires_in?: number; // value in seconds (auth token internal flow)
    initialSetup?: boolean; // auth token internal flow
  };
  recentlyPlayedCategory?: CategoryGameGridWithContentType;
  refreshTokenRetryNumber?: number;
  recentlyPlayedGamesList?: PortalVerticalGameTilePropTypes[];
  balance: number;
  currency: string;
};

export type RecentlyPlayedUpdated = {
  recentlyPlayedGamesList: PortalVerticalGameTilePropTypes[];
};

export type SearchInfo = {
  searchPhrase: string;
  sourceType: SourceTypeEnum;
  grouppedItems: { [key: string]: Array<PortalVerticalGameTilePropTypes> };
  searchItems: Array<PortalVerticalGameTilePropTypes>;
  searchCategory: CategoryGameGridWithContentType;
  subheaderItems: Array<{ title: string; state: 'active' | 'inactive' }>;
  currentType: string;
  itemsNumber: number | null;
  attempts: number;
  userToken: string;
  queryID: string;
};

export type CallbackMode = 'event' | 'native';

export type UserInitData = {
  CustomerAccountNumber: string | null;
  CurrencyCode: string | null;
  UserStatus: 'Customer' | 'Non-Customer';
};

export interface Components {
  [key: string]: {
    type: string;
    properties: Record<string, unknown>;
    children: string[];
  };
}

export interface RenderManifest<Keys = Components> {
  components: Keys;
  children: (keyof Keys)[];
  personalizedBanners?: { container: string; slug?: string }[];
  gameLaunchStrategies: GameLaunchStrategies;
  gameCloseStrategies: GameCloseStrategies;
}

export interface Layout {
  root: string;
  components: Components;
  games: PortalVerticalGameTilePropTypes[];
  gameIds: string[];
}

export interface StaticPageDataComponents {
  Navigation: object;
  CookieBanner: object;
  Footer: object;
  Menu: object;
  BottomNavigation: object;
}

export interface Page {
  id: string;
  properties: {
    pageTitle: string;
  };
  isStaticPage: boolean;
  liveDealerTables?: string[];
  gameLaunchStrategies: LaunchStrategyEnum[];
  gameCloseStrategies: CloseStrategyEnum[];
  trackingInfo?: { [key: string]: string | number | undefined };
  renderManifest: RenderManifest;
  staticPageComponentsData: StaticPageDataComponents;
  seo: {
    title: string;
  };
  games: PortalVerticalGameTilePropTypes[];
  gameIds: string[];
}

export type PartialEventData = {
  [key: string]: null | undefined | string | number | string[];
};

export type DeviceType = 'mobile' | 'tablet' | 'desktop';
